<template>
  <Transition name="slide-fade">
      <div v-if="showValidation" class="auth-input-password-validation-block">
        <div class="hint-container">
          <span :class="passwordHintTextState.color + '--text'">
          {{ $t(passwordHintTextState.text) }}
        </span>
          <v-progress-linear
          :value="progressBar"
          :color="passwordHintTextState.barColor"
          :background-color="passwordHintTextState.barColor"
          :background-opacity="0.3"
          rounded
        ></v-progress-linear>
          <div class="password-hints-box">
            <div
            v-for="(el, index) in passwordHintBlockClasses"
            :key="index"
            class="password-hint"
          >
              <div class="dot-icon" :class="el.class" />
              <span class="parameterTex" :class="el.textColor">
                {{ $t(el.text) }}
              </span>
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script>
import {
  checkPasswordStrength,
  containsLowerLetter,
  containsNumber,
  containsSpecialCharacters,
  containsUpperLetter
} from '@/helpers/string'
import {
  PASSWORD_STATE_NORMAL_KEY,
  PASSWORD_STATE_NORMAL_PROGRESS_BAR_COLOR, PASSWORD_STATE_NORMAL_PROGRESS_TEXT_COLOR,
  PASSWORD_STATE_STRONG_KEY,
  PASSWORD_STATE_STRONG_PROGRESS_BAR_COLOR, PASSWORD_STATE_STRONG_PROGRESS_TEXT_COLOR,
  PASSWORD_STATE_WEAK_KEY,
  PASSWORD_STATE_WEAK_PROGRESS_BAR_COLOR, PASSWORD_STATE_WEAK_PROGRESS_TEXT_COLOR
} from '@/constants/auth/password'

export default {
  name: 'UsersPasswordValidation',

  props: {
    password: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      progressBar: 0
    }
  },

  computed: {
    // ПРОВЕРКА НА НАЛИЧИЯ СИМВОЛОВ
    symbol() {
      return containsSpecialCharacters(this.password)
    },

    // ПРОВЕРКА НА НАЛИЧИЯ ВЕРХНЕГО И НИЖНЕГО РЕГИСТРА
    lowerAndUpperCase() {
      return (
        containsLowerLetter(this.password) && containsUpperLetter(this.password)
      )
    },

    // ПРОВЕРКА НА НАЛИЧИЯ БУКВ И ЦИФР
    charAndNumber() {
      return (
        (containsLowerLetter(this.password) ||
          containsUpperLetter(this.password)) &&
        containsNumber(this.password)
      )
    },

    passwordStrength() {
      return this.updatePasswordStrength(this.password)
    },

    passwordHintTextState() {
      switch (this.passwordStrength) {
        case PASSWORD_STATE_STRONG_KEY:
          return {
            barColor: PASSWORD_STATE_STRONG_PROGRESS_BAR_COLOR,
            color: PASSWORD_STATE_STRONG_PROGRESS_TEXT_COLOR,
            text: 'auth.validation.excellentPassword'
          }
        case PASSWORD_STATE_NORMAL_KEY:
          return {
            barColor: PASSWORD_STATE_NORMAL_PROGRESS_BAR_COLOR,
            color: PASSWORD_STATE_NORMAL_PROGRESS_TEXT_COLOR,
            text: 'auth.validation.goodPassword'
          }
        case PASSWORD_STATE_WEAK_KEY:
          return {
            barColor: PASSWORD_STATE_WEAK_PROGRESS_BAR_COLOR,
            color: PASSWORD_STATE_WEAK_PROGRESS_TEXT_COLOR,
            text: 'auth.validation.weakPassword'
          }
        default:
          return {
            barColor: 'icon-additional',
            color: 'text-caption',
            text: 'auth.validation.passwordSafety'
          }
      }
    },

    passwordHintBlockClasses() {
      const checkLength = this.password.length > 0 && this.password.length < 8
      const checkMaxLength = this.password.length > 0 && this.password.length <= 512
      return [
        {
          class: {
            greenDot: this.password.length >= 8,
            redDot: checkLength
          },
          textColor: {
            textError: checkLength,
            textSuccess: this.password.length >= 8
          },
          text: this.$t('auth.validation.min', { length: 8 })
        },
        {
          class: {
            greenDot: checkMaxLength,
            redDot: this.password.length > 512
          },
          textColor: {
            textError: this.password.length > 512,
            textSuccess: checkMaxLength
          },
          text: this.$t('auth.validation.max', { length: 512 })
        },
        {
          class: { containsCharAndNumber: this.charAndNumber },
          textColor: { textSuccess: this.charAndNumber },
          text: this.$t('auth.validation.charAndNumber')
        },
        {
          class: { containsLowerAndUpperCase: this.lowerAndUpperCase },
          textColor: { textSuccess: this.lowerAndUpperCase },
          text: this.$t('auth.validation.lowerAndUpperCase')
        },
        {
          class: { containsSymbol: this.symbol },
          textColor: { textSuccess: this.symbol },
          text: this.$t('auth.validation.specialSymbols')
        }
      ]
    },

    showValidation() {
      return this.$route.name === 'Registration' || 'ResetPassword'
    }
  },

  watch: {
    password() {
      const isPasswordValid =
        this.passwordStrength === PASSWORD_STATE_STRONG_KEY ||
        this.passwordStrength === PASSWORD_STATE_NORMAL_KEY
        this.$emit('setPasswordState', isPasswordValid)
    }
  },

  methods: {
    updatePasswordStrength() {
      const password = this.password
      const numberOfPassedValidations = checkPasswordStrength(password).filter(
        ({ valid }) => valid
      ).length

      if (password.length) {
        if (numberOfPassedValidations === 4 && password.length >= 8 && password.length <= 512)  {
          this.progressBar = 100
          return PASSWORD_STATE_STRONG_KEY
        } else if (numberOfPassedValidations >= 2 && password.length >= 8 && password.length <= 512) {
          this.progressBar = 66.66
          return PASSWORD_STATE_NORMAL_KEY
        } else {
          if (password.length >= 1 && password.length < 8) {
            this.progressBar = 2
            return PASSWORD_STATE_WEAK_KEY
          }
          this.progressBar = 33.33
          return PASSWORD_STATE_WEAK_KEY
        }
      } else {
        this.progressBar = 0
        return ''
      }
    }
  }
}
</script>

<style scoped lang="scss">
.auth-input-password-validation-block {
  margin-top: 18px;
}

.dot-icon {
  width: 5px;
  height: 5px;
  border-radius: 100%;
  background-color: var(--v-text-caption-base);
}

.parameterTex {
  color: var(--v-text-caption-base);
}

.password-hint {
  display: flex;
  align-items: center;
  gap: 6px;
}

.password-hints-box {
  margin-top: 12px;
  gap: 8px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
}

.textSuccess {
  color: var(--v-text-success-base);
}

.textError {
  color: var(--v-text-error-base);
}

.darkDot {
  background-color: var(--v-text-caption-base);
}

.redDot {
  background-color: var(--v-icon-error-base);
  transition: background-color 0.3s linear;
}

.greenDot,
.containsLowerAndUpperCase,
.containsSymbol,
.containsCharAndNumber {
  background-color: var(--v-text-success-base);
  transition: background-color 0.3s linear;
}

.slide-fade-enter-active {
  transition: all 0.3s ease;
}
.slide-fade-leave-active {
  transition: all 0.5s cubic-bezier(1, 0.5, 0.8, 1);
}
.slide-fade-enter, .slide-fade-leave-to
  /* .slide-fade-leave-active below version 2.1.8 */ {
  transform: translateY(10px);
  opacity: 0;
}
</style>
